/**
 * Generieke focus trap voor modale UI elementen.
 *
 * Houdt focus binnen een container via focusin detectie.
 * Wanneer focus ontsnapt naar buiten de container, redirect
 * terug naar eerste of laatste focusable element.
 *
 * Werkt correct met radio groups (browser Tab behavior),
 * dynamische content (AJAX updates), en alle focusable types.
 *
 * @module modules/focus-trap
 */

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not(:disabled)',
    'input:not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function createFocusTrap(container) {
    function getFocusableElements() {
        return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
            (el) => !el.closest('[hidden]') && el.offsetParent !== null,
        );
    }

    let lastFocusedInContainer = null;

    function handleFocusIn(e) {
        if (container.contains(e.target)) {
            lastFocusedInContainer = e.target;
            return;
        }

        // Focus ontsnapte. Redirect terug.
        const focusable = getFocusableElements();
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        // Als laatste focus in container het eerste element was
        // → gebruiker deed Shift+Tab → stuur naar laatste
        if (lastFocusedInContainer === first) {
            last.focus();
        } else {
            // Tab voorbij laatste → stuur naar eerste
            first.focus();
        }
    }

    return {
        activate(initialFocus = null) {
            lastFocusedInContainer = null;
            document.addEventListener('focusin', handleFocusIn);
            const target = initialFocus || container.querySelector(FOCUSABLE_SELECTOR);
            if (target) {
                target.focus();
                lastFocusedInContainer = target;
            }
        },
        deactivate() {
            document.removeEventListener('focusin', handleFocusIn);
            lastFocusedInContainer = null;
        },
    };
}
